import type { QueryKey } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  CreatorCohortBatchPayload,
  CreatorCohortImportPreviewRow,
  CreatorCohortImportResult,
  CreatorTagColor,
} from "@unbox/api";
import { handleError } from "@/api/error";
import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useCreatorCohorts = () => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();

  return useQuery({
    queryKey: ["creator-cohorts", merchantId, workspaceId],
    queryFn: async () => {
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].$get({
            param: { workspaceId, merchantId },
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].$get({
            param: { merchantId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!merchantId,
    refetchOnWindowFocus: "always",
  });
};

export const useCreatorCohortAssignedCount = (cohortId: string, enabled = true) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["creator-cohorts", scope.merchantId, scope.workspaceId, cohortId, "count"],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"][":cohortId"].count.$get({
            param: { ...scope.param, cohortId },
          })
        : await client.merchant[":merchantId"]["creator-cohorts"][":cohortId"].count.$get({
            param: { ...scope.param, cohortId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: enabled && !!scope.merchantId && !!cohortId,
  });
};

// Parse-only: previews the rows a CSV would import, without writing anything.
export const useCreatorCohortsImportPreview = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (csv: string): Promise<CreatorCohortImportPreviewRow[]> => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].import.preview.$post({
            param: scope.param,
            json: { csv },
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].import.preview.$post({
            param: scope.param,
            json: { csv },
          });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).rows;
    },
  });
};

// Actually persists a previously-previewed CSV import. `cohortEdits` carries any
// per-creator color/name overrides chosen in the preview step, keyed by handle.
export const useCreatorCohortsImportSave = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (input: {
      csv: string;
      cohortEdits?: Record<string, { name: string; color: CreatorTagColor }>;
    }): Promise<CreatorCohortImportResult> => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].import.save.$post({
            param: scope.param,
            json: input,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].import.save.$post({
            param: scope.param,
            json: input,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-cohorts"] });
    },
  });
};

// Fetches the import template CSV and triggers a browser download. The filename
// is taken from the server's Content-Disposition header.
export const useDownloadCreatorCohortTemplate = () => {
  const scope = useScopedMerchantClient();

  return async () => {
    const res = scope.workspaceId
      ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].template.$get({
          param: scope.param,
        })
      : await client.merchant[":merchantId"]["creator-cohorts"].template.$get({ param: scope.param });
    if (!res.ok) throw await handleError(res);

    const blob = await res.blob();
    const filename =
      res.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ?? "creator-cohorts-template.csv";
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
};

export const useBatchSaveCreatorCohorts = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: CreatorCohortBatchPayload) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].batch.$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].batch.$post({
            param: scope.param,
            json: payload,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-cohorts"] });
    },
  });
};

// ---------------------------------------------------------------------------
// Inline cohort picker on creator rows
//
// Unlike tags (many-to-many), a creator has at most ONE cohort. The creator
// explorer / recommendations list responses carry a single `cohortId` per row
// (null when unassigned). The table resolves name/colour from the live cohort
// list (`useCreatorCohorts`) so edits (e.g. recolouring) reflect without
// refetching rows. Optimistic updates patch the row's `cohortId` directly.
// ---------------------------------------------------------------------------

type CohortRow = { creatorId: string; cohortId?: string | null };
type RecommendationRow = { creatorId: string | null; cohortId?: string | null };

const isCreatorOverviewQuery = (key: QueryKey): boolean => key[0] === "creator-overview";
const isMerchantRecommendationsQuery = (key: QueryKey): boolean => key[0] === "merchant-recommendations";
const isBlockedCreatorsQuery = (key: QueryKey): boolean => key[0] === "blocked-creators";
/** These list shapes carry per-creator `cohortId` and render the shared cohort cell. */
const isCohortableRowQuery = (key: QueryKey): boolean =>
  isCreatorOverviewQuery(key) || isMerchantRecommendationsQuery(key) || isBlockedCreatorsQuery(key);

/**
 * Set the cohort of the row matching `creatorId` across every cached list that
 * renders the cohort cell: the creator explorer (`{ creators }`), the
 * recommendations / "Matches" table (`{ recommendations }`) and the blocked
 * creators table (`{ items }`).
 */
const patchCreatorRowCohortId = (creatorId: string, cohortId: string | null) => {
  queryClient.setQueriesData<{ creators: CohortRow[] }>(
    { predicate: (q) => isCreatorOverviewQuery(q.queryKey) },
    (old) =>
      old
        ? {
            ...old,
            creators: old.creators.map((row) => (row.creatorId === creatorId ? { ...row, cohortId } : row)),
          }
        : old,
  );
  queryClient.setQueriesData<{ recommendations: RecommendationRow[] }>(
    { predicate: (q) => isMerchantRecommendationsQuery(q.queryKey) },
    (old) =>
      old
        ? {
            ...old,
            recommendations: old.recommendations.map((row) =>
              row.creatorId === creatorId ? { ...row, cohortId } : row,
            ),
          }
        : old,
  );
  queryClient.setQueriesData<{ items: RecommendationRow[] }>(
    { predicate: (q) => isBlockedCreatorsQuery(q.queryKey) },
    (old) =>
      old
        ? {
            ...old,
            items: old.items.map((row) => (row.creatorId === creatorId ? { ...row, cohortId } : row)),
          }
        : old,
  );
};

/**
 * Set (or clear, with `cohortId: null`) the cohort for one or many creators via
 * the shared `POST /creators` endpoint. The inline picker passes a single
 * creatorId; the multi-select action bar passes the whole selection. Optimistic:
 * cached rows update immediately and revert (with a toast) on failure, then
 * reconcile against the server's authoritative response.
 */
export const useSaveCreatorCohortAssignment = () => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();

  return useMutation({
    mutationFn: async (input: { creatorIds: string[]; cohortId: string | null }) => {
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].creators.$post({
            param: { workspaceId, merchantId },
            json: input,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].creators.$post({
            param: { merchantId },
            json: input,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ predicate: (q) => isCohortableRowQuery(q.queryKey) });
      const previous = queryClient.getQueriesData({ predicate: (q) => isCohortableRowQuery(q.queryKey) });

      for (const creatorId of input.creatorIds) {
        patchCreatorRowCohortId(creatorId, input.cohortId);
      }

      return { previous };
    },
    onError: (_err, _input, ctx) => {
      // Roll back the optimistic patch. The error toast (with the server's
      // message) is shown by the global MutationCache handler.
      ctx?.previous.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSuccess: ({ creators }) => {
      for (const row of creators) {
        patchCreatorRowCohortId(row.creatorId, row.cohortId);
      }
    },
    onSettled: () => {
      // Refresh the master cohort list so assigned counts stay accurate.
      queryClient.invalidateQueries({ queryKey: ["creator-cohorts", merchantId, workspaceId], exact: true });
    },
  });
};

/**
 * Create-on-the-fly: atomically create a cohort (auto-colour, source=manual) and
 * assign it to the creator in one request. The new cohort becomes the row's
 * cohort and is added to the merchant cohort list.
 */
export const useCreateAndAssignCreatorCohort = (creatorId: string) => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();

  return useMutation({
    mutationFn: async (payload: { name: string; color: CreatorTagColor }) => {
      const json = { ...payload, creatorIds: [creatorId] };
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].$post({
            param: { workspaceId, merchantId },
            json,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].$post({
            param: { merchantId },
            json,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (cohort) => {
      patchCreatorRowCohortId(creatorId, cohort.id);
      queryClient.invalidateQueries({ queryKey: ["creator-cohorts", merchantId, workspaceId], exact: true });
    },
    // Errors (e.g. a duplicate name) are surfaced by the global MutationCache handler.
  });
};

// ---------------------------------------------------------------------------
// Bulk cohort assignment from the multi-select action bar
//
// Assignment itself reuses `useSaveCreatorCohortAssignment` (same `POST /creators`
// endpoint) with the full selection. The two hooks below are read/create helpers
// specific to the bulk-apply modal.
// ---------------------------------------------------------------------------

/**
 * Per-cohort assignment counts across the selected creators, for the bulk-apply
 * modal. A cohort whose count equals the selection size is the one the whole
 * selection already shares. Only runs while the modal is open.
 */
export const useCreatorCohortCounts = (creatorIds: string[], enabled = true) => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();
  // Stable key regardless of selection order.
  const sortedIds = [...creatorIds].sort();

  return useQuery({
    queryKey: ["creator-cohort-counts", merchantId, workspaceId, sortedIds],
    queryFn: async () => {
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].creators[
            "cohort-counts"
          ].$post({
            param: { workspaceId, merchantId },
            json: { creatorIds },
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].creators["cohort-counts"].$post({
            param: { merchantId },
            json: { creatorIds },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: enabled && !!merchantId && creatorIds.length > 0,
  });
};

/**
 * Create-on-the-fly from the bulk-apply modal: create a cohort (auto-colour,
 * source=manual) without assigning it. The modal stages it as selected and the
 * single bulk save assigns it to the selection.
 */
export const useCreateCreatorCohort = () => {
  const workspaceId = useWorkspaceId();
  const merchantId = useMerchant();

  return useMutation({
    mutationFn: async (payload: { name: string; color: CreatorTagColor }) => {
      const res = workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].$post({
            param: { workspaceId, merchantId },
            json: payload,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].$post({
            param: { merchantId },
            json: payload,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-cohorts", merchantId, workspaceId], exact: true });
    },
    // Errors (e.g. a duplicate name) are surfaced by the global MutationCache handler.
  });
};
